import React, { useState, useRef, useCallback } from 'react';
import { Alert, Button } from '@mui/material';
import FlexService from '../../services/FlexService';

const humanize = (value) => String(value ?? '')
  .toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const chipLabel = (chips, field, value) =>
  (chips || []).find((c) => c.field === field && String(c.value) === String(value))?.label || humanize(value);

/**
 * A row the user just acted on can drop out of a filtered grid (a receipt received in full closes,
 * and the Open filter hides it). FH-201: say so, with a "Show it" that finds it, instead of the row
 * silently vanishing.
 *
 * watch() when a row action starts; check() whenever that grid's data reloads; reset() when the
 * user changes the filters or search themselves (a row they filtered out isn't news).
 */
export default function useHiddenRowNotice(chips, activeFilters) {
  const [notice, setNotice] = useState(null);
  const watchRef = useRef(null);

  const watch = useCallback(({ source, row, keyField, loadScript, loadParams, data }) => {
    const key = keyField && row ? row[keyField] : null;
    if (key == null || key === '' || !loadScript || !activeFilters?.length) {
      watchRef.current = null;
      return;
    }
    watchRef.current = { source, key: String(key), keyField, loadScript, loadParams, data, filters: activeFilters };
    setNotice(null);
  }, [activeFilters]);

  const check = useCallback(async (source, data) => {
    const w = watchRef.current;
    // only a reload of the grid the action ran in counts
    if (!w || w.source !== source || !Array.isArray(data) || data === w.data) return;
    watchRef.current = null;
    if (data.some((r) => String(r[w.keyField]) === w.key)) return;

    // where did it go? look it up across every status
    let found = null;
    try {
      const response = await FlexService.executeScript({
        scriptName: w.loadScript,
        context: { _queryParams: { ...(w.loadParams || {}), q: w.key } },
      });
      let rows = response.success ? ((response.context || {}).result || (response.context || {}).data) : null;
      if (rows && !Array.isArray(rows)) rows = Array.isArray(rows.data) ? rows.data : [rows];
      found = (rows || []).find((r) => String(r[w.keyField]) === w.key) || null;
    } catch (err) {
      return;
    }
    if (!found) return; // deleted, nothing to show

    // the filter field it no longer matches; if it still matches them all, it's hidden for another reason
    const fields = [...new Set(w.filters.map((f) => f.field))];
    const field = fields.find((f) => !w.filters.some((a) => a.field === f && String(a.value) === String(found[f])));
    if (!field) return;
    setNotice({
      key: w.key,
      keyField: w.keyField,
      status: chipLabel(chips, field, found[field]),
      filters: w.filters.filter((a) => a.field === field).map((a) => chipLabel(chips, a.field, a.value)),
    });
  }, [chips]);

  const reset = useCallback(() => {
    watchRef.current = null;
    setNotice(null);
  }, []);

  return { notice, watch, check, reset, dismiss: () => setNotice(null) };
}

/** The message itself; onShow gets the record's key */
export const HiddenRowNotice = ({ notice, onShow, onClose }) => {
  if (!notice) return null;
  const filterText = notice.filters.length > 1
    ? `the ${notice.filters.slice(0, -1).join(', ')} and ${notice.filters[notice.filters.length - 1]} filters`
    : `the ${notice.filters[0]} filter`;
  return (
    <Alert
      severity="info"
      onClose={onClose}
      data-testid="hidden-row-notice"
      sx={{ mx: 1.5, my: 0.75, py: 0 }}
      action={<Button color="inherit" size="small" onClick={() => onShow(notice.key)}>Show it</Button>}
    >
      {notice.key} is now {notice.status}, so {filterText} hides it.
    </Alert>
  );
};
